import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Container,
  Skeleton,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  InputBase,
  Tabs,
  Tab,
  Pagination,
  Chip,
  Collapse,
  LinearProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CompareArrows as CompareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  Wallet as WalletIcon,
  AttachMoney as MoneyIcon,
  SwapHoriz as ExchangeIcon,
  ShoppingCart as PurchaseIcon,
  Store as SaleIcon,
  MoneyOff as ExpenseIcon,
  Download as WithdrawIcon,
  Upload as DepositIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useGetCashDataQuery, useGetTransactionsQuery } from './Redux/financeApi';
import moment from 'moment-jalaali';
import 'moment-timezone';
import Datepicker_Customer from './forms/Datepicker_customer';

// کامپوننت کارت متحرک
const MotionCard = motion(Card);

// تابع فرمت ارز
const formatCurrency = (amount) => {
  return amount ? new Intl.NumberFormat().format(amount) : '0';
};

// تابع تبدیل تاریخ
const formatDate = (dateString, settings) => {
  if (!dateString) return '';
  
  const userTimeZone = moment.tz?.guess?.() || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const date = moment.utc(dateString).tz(userTimeZone);
  
  const hours = date.hours();
  const minutes = date.minutes();
  let period = hours >= 12 ? "pm" : "am";
  
  if (settings?.date === "Persian") {
    moment.locale("fa");
    period = hours >= 12 ? "ب.ظ" : "ق.ظ";
    const formattedDate = date.format("jYYYY-jMM-jDD");
    return `${formattedDate} ${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${period}`;
  } else {
    moment.locale("en");
    const formattedDate = date.format("YYYY-MM-DD");
    return `${formattedDate} ${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${period}`;
  }
};

// پیکربندی نوع تراکنش‌ها
const transactionConfigs = {
  deposite: {
    icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
    text: 'واریز',
    color: 'success.main',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
  },
  withdraw: {
    icon: <TrendingDownIcon sx={{ fontSize: 16 }} />,
    text: 'برداشت',
    color: 'error.main',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    gradient: 'linear-gradient(135deg, #F44336 0%, #C62828 100%)'
  },
  purchase: {
    icon: <PurchaseIcon sx={{ fontSize: 16 }} />,
    text: 'خرید',
    color: 'info.main',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)'
  },
  expense: {
    icon: <ExpenseIcon sx={{ fontSize: 16 }} />,
    text: 'مصرف',
    color: 'warning.main',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)'
  },
  exchange_withdraw: {
    icon: <WithdrawIcon sx={{ fontSize: 16 }} />,
    text: 'برداشت تبادل',
    color: 'error.dark',
    bgColor: 'rgba(245, 124, 0, 0.1)',
    borderColor: 'rgba(245, 124, 0, 0.3)',
    gradient: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)'
  },
  exchange_deposit: {
    icon: <DepositIcon sx={{ fontSize: 16 }} />,
    text: 'واریز تبادل',
    color: 'success.dark',
    bgColor: 'rgba(46, 125, 50, 0.1)',
    borderColor: 'rgba(46, 125, 50, 0.3)',
    gradient: 'linear-gradient(135deg, #388E3C 0%, #1B5E20 100%)'
  },
  fabric_cash_sale: {
    icon: <SaleIcon sx={{ fontSize: 16 }} />,
    text: 'فروش نقدی پارچه',
    color: 'success.main',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
  },
  fabric_installment: {
    icon: <SaleIcon sx={{ fontSize: 16 }} />,
    text: 'قسط پارچه',
    color: 'info.main',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)'
  },
  tailor_payment: {
    icon: <MoneyIcon sx={{ fontSize: 16 }} />,
    text: 'پرداخت خیاط',
    color: 'warning.main',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)'
  },
  customer_settlement: {
    icon: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
    text: 'تسویه مشتری',
    color: 'success.main',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
  },
  default: {
    icon: <ReceiptIcon sx={{ fontSize: 16 }} />,
    text: 'تراکنش',
    color: 'grey.700',
    bgColor: 'rgba(158, 158, 158, 0.1)',
    borderColor: 'rgba(158, 158, 158, 0.3)',
    gradient: 'linear-gradient(135deg, #9E9E9E 0%, #616161 100%)'
  }
};

// کامپوننت کارت وضعیت مالی
const FinanceStatusCard = ({ account, index, yesterdayData }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  // استایل‌های کارت
  const cardStyles = [
    {
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      text: '#fff',
      accent: '#8a6dde',
      shadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
    },
    {
      bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      text: '#fff',
      accent: '#25b897',
      shadow: '0 8px 32px rgba(17, 153, 142, 0.3)'
    },
    {
      bg: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
      text: '#2c3e50',
      accent: '#ff7b81',
      shadow: '0 8px 32px rgba(255, 154, 158, 0.3)'
    },
    {
      bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      text: '#2c3e50',
      accent: '#7aa6f5',
      shadow: '0 8px 32px rgba(161, 196, 253, 0.3)'
    },
    {
      bg: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      text: '#2c3e50',
      accent: '#fda085',
      shadow: '0 8px 32px rgba(246, 211, 101, 0.3)'
    },
    {
      bg: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
      text: '#2c3e50',
      accent: '#84fab0',
      shadow: '0 8px 32px rgba(132, 250, 176, 0.3)'
    }
  ];

  const cardStyle = cardStyles[index % cardStyles.length];
  const yesterdayAccount = yesterdayData?.find(item => item.id === account.id);
  
  // محاسبه تفاوت
  const difference = account?.cash - (yesterdayAccount?.cash || 0);
  const isPositive = difference >= 0;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={account.id}>
      <MotionCard
        whileHover={{ y: -4, scale: 1.02 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        sx={{
          background: cardStyle.bg,
          color: cardStyle.text,
          borderRadius: 3,
          overflow: 'visible',
          boxShadow: cardStyle.shadow,
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: -10,
            right: 20,
            width: 60,
            height: 60,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
          }
        }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', flexGrow: 1 }}>
          {/* هدر کارت */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WalletIcon fontSize="small" />
                <Typography variant="h6" fontWeight="bold" noWrap>
                  {account.name}
                </Typography>
              </Box>
              
              {/* موجودی فعلی */}
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                {formatCurrency(account.cash)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                color: 'inherit',
                background: 'rgba(255,255,255,0.15)',
                '&:hover': { 
                  background: 'rgba(255,255,255,0.25)',
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.3s ease'
                }
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {/* تفاوت امروز */}
          <Box sx={{
            mb: 2,
            p: 1.5,
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isPositive ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center' }}>
                <CompareIcon sx={{ fontSize: 16, ml: 0.5 }} />
                تفاوت امروز
              </Typography>
              <Chip
                size="small"
                icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isPositive ? '#4CAF50' : '#F44336' }}>
                    {isPositive ? '+' : ''}{formatCurrency(difference)}
                  </Typography>
                }
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Box>
          </Box>

          {/* جزئیات اضافی */}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              {/* واریزی‌ها و برداشت‌ها */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Box sx={{
                    p: 1.5,
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                      واریزی‌ها
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="#4CAF50">
                      +{formatCurrency(account.deposit || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{
                    p: 1.5,
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                      برداشت‌ها
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="#F44336">
                      -{formatCurrency(account.withdraw || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* موجودی دیروز */}
              <Box sx={{
                p: 1.5,
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                mb: 1
              }}>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                  موجودی دیروز
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formatCurrency(yesterdayAccount?.cash || 0)}
                </Typography>
              </Box>

              {/* جزئیات fabrics */}
            </Box>
          </Collapse>
        </CardContent>
      </MotionCard>
    </Grid>
  );
};

// کامپوننت سلول نوع تراکنش
const TransactionTypeCell = ({ type }) => {
  const config = transactionConfigs[type] || transactionConfigs.default;

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1.5,
      py: 0.5,
      borderRadius: 2,
      fontSize: '0.75rem',
      fontWeight: 'bold',
      color: config.color,
      background: config.bgColor,
      border: `1px solid ${config.borderColor}`,
      backdropFilter: 'blur(10px)',
      gap: 0.5
    }}>
      {config.icon}
      {config.text}
    </Box>
  );
};

// کامپوننت سلول مبلغ
const TransactionAmountCell = ({ amount, type }) => {
  const isPositive = ['deposite', 'exchange_deposit', 'fabric_cash_sale', 'customer_settlement', 'fabric_installment'].includes(type);
  
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 0.5
    }}>
      {isPositive ? (
        <ArrowUpwardIcon sx={{ color: 'success.main', fontSize: 16 }} />
      ) : (
        <ArrowDownwardIcon sx={{ color: 'error.main', fontSize: 16 }} />
      )}
      <Typography
        variant="body1"
        fontWeight="bold"
        sx={{
          color: isPositive ? 'success.main' : 'error.main',
          direction: 'ltr'
        }}
      >
        {isPositive ? '+' : '-'}{formatCurrency(Math.abs(amount))}
      </Typography>
    </Box>
  );
};

// کامپوننت اصلی
const CashDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  // const { settings } = useSelector((state) => state.settings);
  const settings=[];
  // State‌ها
  const [selectedDate, setSelectedDate] = useState(moment().startOf('day'));
  const [transactionTab, setTransactionTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // استفاده از RTK Query
  const { data: cashData, isLoading, isError, refetch } = useGetCashDataQuery({
    date: selectedDate.format('YYYY-MM-DD')
  });

  // دسته‌بندی تراکنش‌ها بر اساس تب
  const filteredTransactions = cashData?.transactions?.filter(transaction => {
    if (transactionTab === 0) return true;
    if (transactionTab === 1) return ['deposite', 'exchange_deposit', 'fabric_cash_sale', 'customer_settlement'].includes(transaction.type);
    if (transactionTab === 2) return ['withdraw', 'exchange_withdraw', 'expense', 'tailor_payment'].includes(transaction.type);
    if (transactionTab === 3) return ['exchange_deposit', 'exchange_withdraw'].includes(transaction.type);
    if (transactionTab === 4) return ['fabric_cash_sale', 'fabric_installment'].includes(transaction.type);
    if (transactionTab === 5) return ['purchase'].includes(transaction.type);
    if (transactionTab === 6) return ['expense'].includes(transaction.type);
    return true;
  })?.filter(transaction =>
    transaction.description?.toLowerCase().includes(transactionSearch.toLowerCase()) ||
    transaction.account_name?.toLowerCase().includes(transactionSearch.toLowerCase()) ||
    transaction.money?.toLowerCase().includes(transactionSearch.toLowerCase())
  ) || [];

  // مرتب‌سازی تراکنش‌ها
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortField === 'amount') {
      return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    }
    
    if (sortField === 'date') {
      return sortOrder === 'asc' 
        ? new Date(a.date_created) - new Date(b.date_created)
        : new Date(b.date_created) - new Date(a.date_created);
    }
    
    return sortOrder === 'asc' 
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  // تراکنش‌های صفحه جاری
  const paginatedTransactions = sortedTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Tab labels
  const tabLabels = [
    'همه تراکنش‌ها',
    // 'دریافتی‌ها',
    'پرداختی‌ها',
    'تبادله‌ها',
    'فروش پارچه',
    'خریدها',
    'مصارف'
  ];

  // توابع
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleTransactionTabChange = (event, newValue) => {
    setTransactionTab(newValue);
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };


  const handle_date_end = (jalaliDate) => {
    if (jalaliDate) {
      const { year, month, day } = jalaliDate;
      const now = new Date();
      const date = new Date(
        jalaliDate.year,
        jalaliDate.month - 1,
        jalaliDate.day,
        now.getHours(),
        now.getMinutes()
      );
      const isoString = date.toISOString();
      setDate_end((pre) => isoString);
    }
  };

    const [Date_End, setDate_end] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0); // set time to 00:00:00
    return d.toISOString();
  });

  // نمایش خطا
  if (isError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            خطا در دریافت اطلاعات
          </Typography>
          <Button
            variant="contained"
            onClick={refetch}
            startIcon={<RefreshIcon />}
            sx={{ mt: 2 }}
          >
            تلاش مجدد
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* هدر */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2
          }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                داشبورد نقدینگی
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                مدیریت و رصد وضعیت نقدی سیستم
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon />
                <Typography variant="body2">تاریخ:</Typography>
              </Box>
              <Box>
              <Datepicker_Customer
                selectedDay={selectedDate}
                setSelectedDay={setSelectedDate}
                handle_date={handle_date_end}
                lebal=""
              />
              </Box>
              <Button
                variant="contained"
                onClick={refetch}
                startIcon={<RefreshIcon />}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  '&:hover': { background: 'rgba(255,255,255,0.3)' }
                }}
              >
                بروزرسانی
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* کارت‌های حساب‌های نقدی */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WalletIcon />
            حساب‌های نقدی
          </Typography>
          
          {isLoading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : cashData?.today?.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <AccountBalanceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                حساب نقدی یافت نشد
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              <AnimatePresence>
                {cashData?.today?.map((account, index) => (
                  <FinanceStatusCard
                    key={account.id}
                    account={account}
                    index={index}
                    yesterdayData={cashData.yesterday}
                  />
                ))}
              </AnimatePresence>
            </Grid>
          )}
        </Box>

        {/* جدول تراکنش‌ها */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)'
          }}
        >
          {/* هدر جدول */}
          <Box sx={{
            p: 3,
            background: 'linear-gradient(90deg, #6a11cb 0%, #2575fc 100%)',
            color: 'white'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2
            }}>
              <Typography variant="h5" fontWeight="bold">
                تراکنش‌های امروز
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Paper
                  component="form"
                  sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    width: { xs: '100%', sm: 250 },
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: 2
                  }}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1, color: 'white' }}
                    placeholder="جستجوی تراکنش..."
                    value={transactionSearch}
                    onChange={(e) => setTransactionSearch(e.target.value)}
                  />
                  <IconButton type="button" sx={{ p: '10px', color: 'white' }}>
                    <SearchIcon />
                  </IconButton>
                </Paper>
              </Box>
            </Box>
          </Box>

          {/* تب‌ها */}
          <Tabs
            value={transactionTab}
            onChange={handleTransactionTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 3,
              pt: 2,
              '& .MuiTab-root': {
                borderRadius: 2,
                mx: 0.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(106, 17, 203, 0.1)'
                }
              },
              '& .Mui-selected': {
                color: '#6a11cb',
                fontWeight: 'bold'
              }
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>

          {/* جدول */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortField === 'id'}
                      direction={sortOrder}
                      onClick={() => handleSort('id')}
                    >
                      #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortField === 'date'}
                      direction={sortOrder}
                      onClick={() => handleSort('date')}
                    >
                      تاریخ
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>شرح</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>حساب</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    <TableSortLabel
                      active={sortField === 'amount'}
                      direction={sortOrder}
                      onClick={() => handleSort('amount')}
                    >
                      مبلغ
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">نوع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        تراکنشی یافت نشد
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTransactions.map((transaction, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(106, 17, 203, 0.05)'
                        }
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        {formatDate(transaction.date_created, settings)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'rgba(106, 17, 203, 0.1)',
                            color: '#6a11cb'
                          }}>
                            <ReceiptIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {transaction.description}
                            </Typography>
                            {transaction.source && (
                              <Typography variant="caption" color="text.secondary">
                                منبع: {transaction.source}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {transaction.account_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transaction.money}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <TransactionAmountCell
                          amount={transaction.amount}
                          type={transaction.type}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TransactionTypeCell type={transaction.type} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* صفحه‌بندی */}
          <Box sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="body2" color="text.secondary">
              تعداد کل: {filteredTransactions.length} تراکنش
            </Typography>
            <Pagination
              count={Math.ceil(filteredTransactions.length / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        </Paper>

        {/* خلاصه اطلاعات */}
        {cashData?.today && (
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">تعداد حساب‌ها</Typography>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {cashData.today.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">کل تراکنش‌ها</Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {cashData.transactions?.length || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">جمع واریزی‌ها</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {formatCurrency(cashData.today.reduce((sum, acc) => sum + (acc.deposit || 0), 0))}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">جمع برداشت‌ها</Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {formatCurrency(cashData.today.reduce((sum, acc) => sum + (acc.withdraw || 0), 0))}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CashDashboard;